import { casoConfig, ocirConfig, scaredConfig, scasConfig } from './configs/anxiety-scales';
import { cabiConfig } from './configs/cabi';
import {
    apsrConfig,
    coreomConfig,
    dass21Config,
    pcl5Config,
    snapivConfig,
} from './configs/multiscale';
import {
    aqConfig,
    dersConfig,
    gadlConfig,
    qdepConfig,
    uppspConfig,
} from './configs/profile-scales';
import {
    aq10Config,
    hcl32Config,
    mchatrConfig,
    mdqConfig,
    qfcConfig,
} from './configs/screening-totals';
import {
    auditConfig,
    cesdConfig,
    ftndConfig,
    gds15Config,
    mhi5Config,
    pfefferConfig,
    pswqConfig,
    rsesConfig,
    scoffConfig,
    srq20Config,
    swlsConfig,
} from './configs/simple-totals';
import {
    domainColumns,
    formatOutOf,
    formatYesNo,
    getNumber,
    getRecord,
    getScale,
    getScales,
    getString,
    mapBingeLabel,
    mapClinicalLabel,
    mapRiskLabel,
    mapScreeningLabel,
    type OutcomeTableColumn,
    type OutcomeTableConfig,
} from './helpers';

const asrs18Columns: OutcomeTableColumn[] = [
    { key: 'factor', label: 'Fatores Avaliados' },
    { key: 'score', label: 'Pontuação' },
    { key: 'classification', label: 'Classificação' },
];

type Bis11ScaleRow = {
    scale: string;
    raw: number | null;
    z_score: number | null;
    percentile: number | null;
    interpretation: string | null;
    interpretation_by_sd: string | null;
};

const BIS11_SCALE_LABELS: Record<string, string> = {
    total: 'Total',
    non_planning: 'Não planejamento',
    inhibitory_control: 'Controle inibitório',
};

const asrs18Config: OutcomeTableConfig = {
    title: 'Escalas ASRS-18',
    columns: asrs18Columns,
    getRows: (outcomes) => {
        const scales = getScales(outcomes);
        const inattention = getScale(scales, 'inattention');
        const hyperactivity = getScale(scales, 'hyperactivity');

        return [
            {
                factor: 'Desatenção',
                score: formatOutOf(inattention.symptoms, 9),
                classification: mapClinicalLabel(inattention.clinical),
            },
            {
                factor: 'Hiperatividade/Impulsividade',
                score: formatOutOf(hyperactivity.symptoms, 9),
                classification: mapClinicalLabel(hyperactivity.clinical),
            },
        ];
    },
    match: (outcomes) => {
        const scales = getScales(outcomes);
        const inattention = getScale(scales, 'inattention');
        const hyperactivity = getScale(scales, 'hyperactivity');
        const total = getScale(scales, 'total');

        return (
            'symptoms' in inattention &&
            'clinical' in inattention &&
            'symptoms' in hyperactivity &&
            'clinical' in hyperactivity &&
            'symptoms' in total
        );
    },
};

const bis11Config: OutcomeTableConfig = {
    title: 'Escalas BIS-11',
    columns: [
        { key: 'scale', label: 'Escala' },
        { key: 'raw', label: 'Bruto' },
        { key: 'z_score', label: 'Z-score' },
        { key: 'percentile', label: 'Percentil' },
        { key: 'interpretation', label: 'Interpretação' },
        { key: 'interpretation_by_sd', label: 'Interpretação (DP)' },
    ],
    getRows: (outcomes) => {
        const scales = getScales(outcomes);

        return Object.entries(scales).map(([key, scale]) => {
            const value = getRecord(scale);

            return {
                scale: BIS11_SCALE_LABELS[key] || key,
                raw: getNumber(value.raw),
                z_score: getNumber(value.z_score),
                percentile: getNumber(value.percentile),
                interpretation: getString(value.interpretation),
                interpretation_by_sd: getString(value.interpretation_by_sd),
            };
        }) as Bis11ScaleRow[];
    },
    match: (outcomes) => {
        const scales = getScales(outcomes);
        const scaleKeys = Object.keys(scales);
        return (
            scaleKeys.includes('total') &&
            scaleKeys.includes('non_planning') &&
            scaleKeys.includes('inhibitory_control')
        );
    },
};

const phq9Config: OutcomeTableConfig = {
    title: 'Resultados PHQ-9',
    columns: domainColumns,
    getRows: (outcomes) => {
        const scales = getScales(outcomes);
        const total = getScale(scales, 'total');
        const risk = getScale(scales, 'risk');
        const functionalImpairment = getScale(scales, 'functional_impairment');

        return [
            {
                domain: 'Sintomas depressivos (total PHQ-9)',
                result: formatOutOf(total.raw, 27),
                interpretation: getString(total.severity_label),
            },
            {
                domain: 'Ideação de morte/autoagressão (item 9)',
                result: getNumber(risk.suicidal_ideation_raw),
                interpretation: mapRiskLabel(risk.suicidal_ideation_positive),
            },
            {
                domain: 'Impacto funcional (item 10)',
                result: getNumber(functionalImpairment.raw),
                interpretation: getString(functionalImpairment.label),
            },
        ];
    },
    match: (outcomes) => {
        const scales = getScales(outcomes);
        const total = getScale(scales, 'total');
        const risk = getScale(scales, 'risk');
        const functionalImpairment = getScale(scales, 'functional_impairment');

        return (
            'severity_label' in total &&
            'suicidal_ideation_raw' in risk &&
            'suicidal_ideation_positive' in risk &&
            'label' in functionalImpairment
        );
    },
};

const gad7Config: OutcomeTableConfig = {
    title: 'Resultados GAD-7',
    columns: domainColumns,
    getRows: (outcomes) => {
        const scales = getScales(outcomes);
        const total = getScale(scales, 'total');
        const functionalImpairment = getScale(scales, 'functional_impairment');

        return [
            {
                domain: 'Sintomas ansiosos (total GAD-7)',
                result: formatOutOf(total.raw, 21),
                interpretation: getString(total.severity_label),
            },
            {
                domain: 'Rastreio clínico (ponto de corte >= 10)',
                result: formatYesNo(total.screening_positive),
                interpretation: mapScreeningLabel(total.screening_positive),
            },
            {
                domain: 'Impacto funcional (item 8)',
                result: getNumber(functionalImpairment.raw),
                interpretation: getString(functionalImpairment.label),
            },
        ];
    },
    match: (outcomes) => {
        const scales = getScales(outcomes);
        const total = getScale(scales, 'total');
        const functionalImpairment = getScale(scales, 'functional_impairment');

        return (
            'severity_label' in total &&
            'screening_positive' in total &&
            'label' in functionalImpairment
        );
    },
};

const auditcConfig: OutcomeTableConfig = {
    title: 'Resultados AUDIT-C',
    columns: domainColumns,
    getRows: (outcomes) => {
        const scales = getScales(outcomes);
        const total = getScale(scales, 'total');
        const patterns = getScale(scales, 'patterns');

        return [
            {
                domain: 'Consumo de álcool (total AUDIT-C)',
                result: formatOutOf(total.raw, 12),
                interpretation: getString(total.severity_label),
            },
            {
                domain: 'Rastreio clínico (ponto de corte >= 4)',
                result: formatYesNo(total.screening_positive),
                interpretation: mapScreeningLabel(total.screening_positive),
            },
            {
                domain: 'Episódio de 6 ou mais doses (item 3)',
                result: getNumber(patterns.binge_raw),
                interpretation: mapBingeLabel(patterns.binge_present),
            },
        ];
    },
    match: (outcomes) => {
        const scales = getScales(outcomes);
        const total = getScale(scales, 'total');
        const patterns = getScale(scales, 'patterns');

        return (
            'severity_label' in total &&
            'screening_positive' in total &&
            'frequency_raw' in patterns &&
            'quantity_raw' in patterns &&
            'binge_raw' in patterns
        );
    },
};

const catqConfig: OutcomeTableConfig = {
    title: 'Resultados CAT-Q',
    columns: domainColumns,
    getRows: (outcomes) => {
        const scales = getScales(outcomes);
        const total = getScale(scales, 'total');
        const compensation = getScale(scales, 'compensation');
        const masking = getScale(scales, 'masking');
        const assimilation = getScale(scales, 'assimilation');

        return [
            {
                domain: 'Camuflagem social (total CAT-Q)',
                result: formatOutOf(total.raw, 175),
                interpretation: getString(total.interpretation_label),
            },
            {
                domain: 'Compensação',
                result: formatOutOf(compensation.raw, 63),
                interpretation: 'Estratégias para compensar dificuldades em interações sociais',
            },
            {
                domain: 'Mascaramento',
                result: formatOutOf(masking.raw, 56),
                interpretation: 'Estratégias para ocultar traços autísticos em situações sociais',
            },
            {
                domain: 'Assimilação',
                result: formatOutOf(assimilation.raw, 56),
                interpretation: 'Esforço para se adaptar e parecer socialmente integrado(a)',
            },
        ];
    },
    match: (outcomes) => {
        const scales = getScales(outcomes);
        const total = getScale(scales, 'total');
        const compensation = getScale(scales, 'compensation');
        const masking = getScale(scales, 'masking');
        const assimilation = getScale(scales, 'assimilation');

        return (
            'interpretation_label' in total &&
            'raw' in compensation &&
            'raw' in masking &&
            'raw' in assimilation
        );
    },
};

const outcomeConfigs: Array<{ id: string; config: OutcomeTableConfig }> = [
    { id: 'apsr', config: apsrConfig },
    { id: 'aq', config: aqConfig },
    { id: 'aq10', config: aq10Config },
    { id: 'asrs18', config: asrs18Config },
    { id: 'audit', config: auditConfig },
    { id: 'auditc', config: auditcConfig },
    { id: 'bis11', config: bis11Config },
    { id: 'cabi', config: cabiConfig },
    { id: 'caso', config: casoConfig },
    { id: 'catq', config: catqConfig },
    { id: 'cesd', config: cesdConfig },
    { id: 'coreom', config: coreomConfig },
    { id: 'dass21', config: dass21Config },
    { id: 'ders', config: dersConfig },
    { id: 'ftnd', config: ftndConfig },
    { id: 'gad7', config: gad7Config },
    { id: 'gadl', config: gadlConfig },
    { id: 'gds15', config: gds15Config },
    { id: 'hcl32', config: hcl32Config },
    { id: 'mchatr', config: mchatrConfig },
    { id: 'mdq', config: mdqConfig },
    { id: 'mhi5', config: mhi5Config },
    { id: 'ocir', config: ocirConfig },
    { id: 'pcl5', config: pcl5Config },
    { id: 'pfeffer', config: pfefferConfig },
    { id: 'phq9', config: phq9Config },
    { id: 'pswq', config: pswqConfig },
    { id: 'qdep', config: qdepConfig },
    { id: 'qfc', config: qfcConfig },
    { id: 'rses', config: rsesConfig },
    { id: 'scared', config: scaredConfig },
    { id: 'scaredp', config: scaredConfig },
    { id: 'scas', config: scasConfig },
    { id: 'scasp', config: scasConfig },
    { id: 'scoff', config: scoffConfig },
    { id: 'snapiv', config: snapivConfig },
    { id: 'srq20', config: srq20Config },
    { id: 'swls', config: swlsConfig },
    { id: 'uppsp', config: uppspConfig },
];

const normalizeTestId = (value?: string) =>
    value ? value.toLowerCase().replace(/[^a-z0-9]/g, '') : '';

export const getOutcomeConfig = (testId?: string, outcomes?: Record<string, unknown> | null) => {
    const normalized = normalizeTestId(testId);
    const matchedById = outcomeConfigs.find((item) => item.id === normalized);
    if (matchedById) return matchedById.config;
    if (outcomes) {
        const matchedByOutcome = outcomeConfigs.find((item) => item.config.match?.(outcomes));
        return matchedByOutcome?.config || null;
    }
    return null;
};
